import axios from 'axios';
import React, { useMemo } from 'react';

import { AppContext } from '../App';
import { ProductEndpointData } from '../AIS/Products';
import './index.scss';
import { InternalOptions } from '../types';
import { ReturnToHomeButton } from '../Header';
import { AlertError, Button, Select, TextInput } from 'plaid-threads';
import {
  PRODUCT_ENDPOINT_OPTIONS,
  ProductEndpointsType,
} from '../AIS/productEndpoints';
import { startUpdateMode } from '../UpdateMode';

interface AISProps {}

const AISV2: React.FC<AISProps> = () => {
  const {
    dispatch,
    countryCode,
    page,
    successfulExtraction,
    forceDesktop,
    accountProducts,
    language,
    transactionDays,
    accountType,
    customizationName,
  } = React.useContext(AppContext);
  const [error, setError] = React.useState('');
  const internalOptions: InternalOptions = useMemo(() => ({}), []);
  if (forceDesktop) {
    internalOptions.forceDesktop = true;
  }

  React.useEffect(() => {
    axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/token/create_link_token`,
        {
          countryCode,
          isIdentityMatch: false,
          accountProducts,
          language: language,
          transactionDays: transactionDays,
          customizationName: customizationName,
        },
        { withCredentials: true },
      )
      .then((response) => {
        if (response.data.item_created) {
          dispatch({
            type: 'SET_SUCCESSFUL_EXTRACTION',
            payload: {
              successfulExtraction: true,
            },
          });
        }
        if (response.data.link_token) {
          // @ts-ignore
          let plaidLink = Plaid.create(
            {
              token: response.data.link_token,
              // @ts-ignore
              onSuccess: async (public_token, metadata) => {
                axios
                  .post(
                    `${process.env.REACT_APP_SERVER_URL}/token/get_access_token`,
                    {
                      publicToken: public_token,
                      products: accountProducts,
                      options: {
                        days_requested: transactionDays,
                      },
                    },
                    { withCredentials: true },
                  )
                  .then((accessTokenResponse) => {
                    if (accessTokenResponse.data.error != null) {
                      setError(error);
                    }
                    dispatch({
                      type: 'SET_SUCCESSFUL_EXTRACTION',
                      payload: {
                        successfulExtraction: true,
                      },
                    });
                  });
              },
              // @ts-ignore
              onExit: async (err, metadata) => {
                plaidLink = undefined;
                // Return to the Landing page for any error or exit event from Link.
                dispatch({
                  type: 'SET_PAGE',
                  payload: {
                    page: 'landingV2',
                  },
                });
              },
              // @ts-ignore
              onEvent: () => {},
            },
            internalOptions,
          );
          plaidLink.open();
          // Change Link's zIndex so the demo banner can be seen
          const iframes = Array.from(document.getElementsByTagName('iframe'));
          iframes.forEach((iframe) => (iframe.style.zIndex = '2147483646'));
        } else {
          setError(response.data.error);
        }
      })
      .catch((error) => {
        setError(error);
      });
  }, [
    dispatch,
    page,
    setError,
    countryCode,
    error,
    accountProducts,
    internalOptions,
    language,
    transactionDays,
    customizationName,
  ]);

  return successfulExtraction ? <AISV2Inner /> : <></>;
};

const generateAISText = (selectedProduct: ProductEndpointsType): string => {
  switch (selectedProduct) {
    case 'auth':
      return 'With Auth, you can also access Identity and Balance data';
    case 'transactions':
      return 'With Transactions, you can also access Auth, Identity and Balance data';
    case 'identity':
      return 'With Identity, you can also access Auth, Balance, and Identity Match data';
    case 'assets':
      return 'With the Financial Insights Report, you can view and download a PDF.';
    case 'income_verification':
      return 'You can now see the Income data returned';
    case 'investments':
      return 'With Investments, you can see holdings, securities, and investment transactions (Alpha — not yet generally available)';
    case 'international_base_report':
      return 'Fetching your International Base Report data, this may take a moment...';
    case 'international_income_insights':
      return 'Fetching your International Income Insights data, this may take a moment...';
    case 'identitymatch':
      return 'With Identity Match, you can get a match score for the name you provide against the name on the bank account.';
  }
  return '';
};

const AISV2Inner: React.FC = () => {
  const { accountProducts, countryCode, transactionDays, pfcVersion } =
    React.useContext(AppContext);

  const [selectedProduct, setSelectedProduct] =
    React.useState<ProductEndpointsType>(
      accountProducts[0] as ProductEndpointsType,
    );
  const [error, setError] = React.useState('');
  const [userFullName, setUserFullName] = React.useState('');
  const [userFullNameReady, setUserFullNameReady] = React.useState(false);
  const [updateModeRunning, setUpdateModeRunning] = React.useState(false);

  const productOptions =
    accountProducts.length === 1 && accountProducts[0] === 'investments'
      ? []
      : [
          {
            label: 'Auth',
            value: 'auth',
          },
          {
            label: 'Transactions',
            value: 'transactions',
          },
          {
            label: 'Identity',
            value: 'identity',
          },
          {
            label: 'Identity Match',
            value: 'identitymatch',
          },
        ];

  PRODUCT_ENDPOINT_OPTIONS.forEach((products) => {
    if (
      accountProducts.includes(products.value) &&
      !productOptions.map((p) => p.value).includes(products.value)
    ) {
      productOptions.push(products);
    }
  });

  const onClickUpdateMode = function () {
    if (updateModeRunning) {
      return;
    }
    setUpdateModeRunning(true);
    startUpdateMode('', countryCode, true)
      .catch((err) => {
        console.error(err);
      })
      .then(() => {
        setUpdateModeRunning(false);
      });
  };

  return (
    <div className="Account">
      <div className="Account__wrapper">
        <div className="AISV2__title">You are all done!</div>
        <div className="Products__instructions">
          Select below to see the data returned.
        </div>
        <Select
          id="productEndpointSelection"
          className="Products__selection"
          controlClassName={error ? 'error' : ''}
          label="Product Endpoint"
          options={productOptions}
          value={PRODUCT_ENDPOINT_OPTIONS.find(
            (p) => p.value === selectedProduct,
          )}
          onChange={(e: any) => setSelectedProduct(e.value)}
        />
        <div className="Products__instructions">
          {generateAISText(selectedProduct)}
          {selectedProduct === 'identitymatch' && (
            <div style={{ marginTop: '20px' }}>
              <TextInput
                id="full name"
                label="Full Name"
                onChange={(e) => {
                  setUserFullName(e.target.value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setUserFullNameReady(true);
                  }
                }}
                placeholder="Full Name"
              ></TextInput>
              <div style={{ translate: '0 -15px' }}>
                <Button
                  onClick={() => {
                    setUserFullNameReady(true);
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: '20px 0px' }}>
          Want to try another Product? Let's go.
        </div>
        <ReturnToHomeButton />
        <div style={{ padding: '10px 0px' }}>
          <Button secondary onClick={onClickUpdateMode}>
            Test Update Mode
          </Button>
        </div>
      </div>
      <div className="Account__products">
        <ProductEndpointData
          product={selectedProduct}
          setError={setError}
          userFullName={userFullNameReady ? userFullName : ''}
          transactionDays={transactionDays}
          pfcVersion={pfcVersion}
        />
        {error && (
          <div className="Products__error">
            <span className="Products__errorIcon">
              <AlertError />
            </span>
            Error while processing. Please choose a different product.
          </div>
        )}
      </div>
    </div>
  );
};

export default AISV2;
